import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import type { UserActivityItem } from '../../types/tracking';
import { formatUserLabel, getInitials } from '../../utils/mapHelpers';

/**
 * HTML-escape a string before injecting into a Leaflet tooltip / popup,
 * which is built via raw HTML strings. Names and roles come from the API
 * and could in theory contain a stray `<` or `&` that would corrupt the
 * markup, so we sanitise at the boundary.
 */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

interface AggregatedViewProps {
  activities: UserActivityItem[];
  onSelect?: (userId: string, multiSelect: boolean) => void;
}

function createAvatarIcon(initials: string, isLive: boolean) {
  const bgColor = isLive ? '#16a34a' : '#9ca3af';
  return L.divIcon({
    className: '',
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:${bgColor};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;font-weight:600;
      border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">${initials}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

function createClusterIcon(cluster: L.MarkerCluster) {
  const count = cluster.getChildCount();
  return L.divIcon({
    className: '',
    html: `<div style="
      width:40px;height:40px;border-radius:50%;
      background:#4f46e5;color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-size:14px;font-weight:700;
      border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);
    ">${count}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

export const AggregatedView: React.FC<AggregatedViewProps> = ({ activities, onSelect }) => {
  const map = useMap();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {
    // Clean up previous cluster group
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    const usersWithLocation = activities.filter(
      a => a.last_latitude != null && a.last_longitude != null
    );

    if (usersWithLocation.length === 0) return;

    const clusterGroup = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
    });

    usersWithLocation.forEach(a => {
      const initials = getInitials(a.name);
      const marker = L.marker([a.last_latitude!, a.last_longitude!], {
        icon: createAvatarIcon(initials, a.is_live),
      });

      const maxRoles = 3;
      const visibleRoles = escapeHtml(a.role.slice(0, maxRoles).join(', '));
      const extraCount = a.role.length - maxRoles;
      const roleText = a.role.length > 0
        ? visibleRoles + (extraCount > 0 ? ` <span style="color:#16a34a;font-weight:500">+${extraCount} more</span>` : '')
        : '';
      const label = escapeHtml(formatUserLabel(a.name, a.employee_id));
      marker.bindTooltip(
        `<strong>${label}</strong>${roleText ? `<br/>${roleText}` : ''}<br/><em style="font-size:11px;color:#6b7280">Click to view route</em>`,
        { direction: 'top', offset: [0, -16] }
      );

      if (onSelect) {
        marker.on('click', (e: L.LeafletMouseEvent) => {
          onSelect(a.user_id, e.originalEvent.ctrlKey || e.originalEvent.metaKey);
        });
      }

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    // Fit bounds to markers
    if (usersWithLocation.length > 0) {
      const bounds = L.latLngBounds(
        usersWithLocation.map(a => [a.last_latitude!, a.last_longitude!] as [number, number])
      );
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
      }
    };
  }, [activities, map]);

  return null;
};
